// Профиль (UX-вайрфрейм, экран 04): портрет только из пресетов игры, ник
// редактируемый. Залогиненному ник сохраняется PATCH /me (displayName),
// гостю — в локальные prefs. Выбранный портрет сразу виден в шапке карты.

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authStore, useAuth } from '@/auth/authStore';
import { AVATARS } from '@/meta/avatars';
import { AvatarFace, SubHeader } from '@/meta/Shell';
import { setPrefs, usePrefs } from '@/meta/prefsStore';

export function ProfileScreen() {
  const auth = useAuth();
  const prefs = usePrefs();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false); // Esc: гасим onBlur-коммит уходящего инпута

  const isAuthed = auth.status === 'authed' && !!auth.me;
  const name = isAuthed ? auth.me!.displayName : prefs.guestName;

  function startEdit() {
    setDraft(name);
    setError(null);
    setEditing(true);
  }

  async function commit() {
    const next = draft.trim().slice(0, 40);
    if (!next || next === name) { setEditing(false); return; }
    if (!isAuthed) {
      setPrefs({ guestName: next });
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await authStore.patchMe({ displayName: next });
      setEditing(false);
    } catch (e) {
      setError(`Ник не сохранился: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  function pickAvatar(id: string) {
    setPrefs({ avatarId: id });
    // best-effort зеркало в player_state.settings — чтение всё равно локальное
    if (isAuthed) void authStore.patchMe({ settings: { avatar: id } }).catch(() => {});
  }

  async function logout() {
    await authStore.logout();
    navigate('/', { replace: true });
  }

  return (
    <main className="meta-screen" data-testid="screen-profile">
      <SubHeader title="Профиль" />
      <div className="meta-body">
        <div className="profile-grid">
          <section>
            <AvatarFace avatarId={prefs.avatarId} large />
            <p className="field-label" style={{ marginTop: 12 }}>Портрет — из набора игры</p>
            <div className="avatar-grid" role="group" aria-label="Выбор портрета">
              {AVATARS.map((a) => (
                <button
                  key={a.id}
                  className="avatar-option"
                  aria-pressed={prefs.avatarId === a.id}
                  data-testid={`avatar-${a.id}`}
                  onClick={() => pickAvatar(a.id)}
                >
                  <AvatarFace avatarId={a.id} />
                  {a.name}
                </button>
              ))}
            </div>
            <p className="hint" style={{ marginTop: 10 }}>
              Свою картинку загрузить нельзя — только пресеты вселенной.
            </p>
          </section>

          <section>
            <p className="field-label">Никнейм</p>
            <div className="nick-row">
              {editing ? (
                <input
                  autoFocus
                  data-testid="nick-input"
                  defaultValue={draft}
                  maxLength={40}
                  aria-label="Никнейм"
                  disabled={saving}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => {
                    if (cancelRef.current) { cancelRef.current = false; return; }
                    void commit();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void commit();
                    if (e.key === 'Escape') { cancelRef.current = true; setEditing(false); }
                  }}
                />
              ) : (
                <strong data-testid="nick-view">{name}</strong>
              )}
              {!editing && (
                <button
                  className="icon-btn"
                  aria-label="Редактировать никнейм"
                  data-testid="nick-edit"
                  onClick={startEdit}
                >✎</button>
              )}
            </div>
            <p className="hint" style={{ margin: '8px 0 18px' }}>Enter — сохранить, Esc — отмена.</p>
            {error && <output className="error-box">{error}</output>}

            {!isAuthed && (
              <div className="guest-box" data-testid="guest-box">
                <span className="badge">ГОСТЕВАЯ СЕССИЯ</span>
                <p className="hint" style={{ color: 'var(--ink)' }}>
                  Сейв живёт в этом браузере. Привяжи аккаунт — прогресс сольётся с серверным.
                </p>
                <Link to="/login" className="btn btn-sm btn-accent" data-testid="claim-link">
                  Привязать аккаунт
                </Link>
              </div>
            )}

            {isAuthed && (
              <p style={{ marginTop: 24 }}>
                <button className="btn btn-sm btn-danger" data-testid="logout-btn" onClick={() => void logout()}>
                  Выйти из аккаунта
                </button>
              </p>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
